import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";

let isInitialized = false;

/** Errors whose message contains any of these strings are not reported */
const IGNORE_BUGSNAG_ERROR: string[] = ["Minified React error"];

/**
 * Release stage for Bugsnag (prod / beta / dev), from NEXT_PUBLIC_CLIENT_ENV.
 */
function getReleaseStage(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const clientEnv = process.env.NEXT_PUBLIC_CLIENT_ENV;
  if (clientEnv === "prod") {
    return "prod";
  }
  if (clientEnv === "beta") {
    return "beta";
  }
  return "dev";
}

export function initBugsnag(): typeof Bugsnag | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (isInitialized) {
    return Bugsnag;
  }

  const releaseStage = getReleaseStage();
  if (!releaseStage) {
    return null;
  }

  const apiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;

  if (!apiKey) {
    if (releaseStage === "dev") {
      console.warn("[Bugsnag] API key not configured, skipping initialization");
      return null;
    }
    console.error("[Bugsnag] API key is required for prod/beta builds");
    return null;
  }

  try {
    Bugsnag.start({
      apiKey,
      plugins: [new BugsnagPluginReact()],
      enabledReleaseStages: ["prod", "beta", "dev"],
      releaseStage,
      collectUserIp: true,
      onError: (event) => {
        const errorMessage = event.errors?.[0]?.errorMessage || "";
        const ignore = IGNORE_BUGSNAG_ERROR.some((item) =>
          String(errorMessage).includes(item),
        );
        if (ignore) {
          return false;
        }

        event.addMetadata("environment", {
          app: "trex-2b",
          clientEnv: process.env.NEXT_PUBLIC_CLIENT_ENV,
          nodeEnv: process.env.NODE_ENV,
        });
      },
    });

    BugsnagPerformance.start({ apiKey });

    isInitialized = true;
    return Bugsnag;
  } catch (error) {
    console.error("[Bugsnag] Failed to initialize:", error);
    return null;
  }
}

export function getBugsnagClient(): typeof Bugsnag | null {
  if (typeof window === "undefined") {
    return null;
  }
  return Bugsnag;
}

/** Observability: low-noise “success” signal in Bugsnag (severity info, not unhandled). */
export function notifyInfo(
  message: string,
  metadata?: Record<string, unknown>,
): void {
  try {
    const client = getBugsnagClient();
    if (!client) {
      return;
    }
    client.notify(new Error(message), (event) => {
      event.severity = "info";
      event.unhandled = false;
      if (metadata) {
        event.addMetadata("info", metadata);
      }
    });
  } catch (e) {
    console.error("[Bugsnag] notifyInfo failed:", e);
  }
}

export function sendError(
  error: Error | string | unknown,
  context?: Record<string, unknown>,
): void {
  try {
    const client = getBugsnagClient();

    if (client) {
      let notifyError: Error | string;

      if (error instanceof Error || typeof error === "string") {
        notifyError = error;
      } else {
        try {
          notifyError = new Error(JSON.stringify(error));
        } catch {
          notifyError = new Error(String(error));
        }
      }

      client.notify(notifyError, (event) => {
        if (context) {
          event.addMetadata("context", context);
        }
      });
    }
  } catch (e) {
    console.error("[Bugsnag] Failed to report error:", e);
  }
}
